import { useEffect, useState, FormEvent, useContext } from "react";
import { getTokenForWeb, getCustomerDetailsByToken, waiterLoginOps } from "../services/auth";
import { checkWaiterAccess, IWaiterAccess } from "../services/checkRole";
import { decrypt, encrypt, getOrCreateDeviceId, validUserLogin } from "../utils/Util";
import { IUserLogin } from "../models/IUserLogin";
import { ILoginInfo } from "../models/ILoginInfo";
import { Context } from "../utils/AppContext";
import { useNavigate } from "react-router-dom";
import LoginRoleSwitch from "../components/LoginRoleSwitch";

const ACCENT = "linear-gradient(135deg, #14b8a6, #0f766e)";
const ACCENT_SHADOW = "0 4px 14px rgba(15,118,110,0.35)";

/** Backend'in garson girişine özel hata kodları — sunucu metni yerine anlaşılır Türkçe. */
const ERROR_TEXT: Record<string, string> = {
  WaiterRoleNotFound: "Bu hesap bir garson hesabı değil. Restoran yöneticisiyseniz \"Restoran\" sekmesinden giriş yapın.",
  WaiterRestaurantLinkMissing: "Hesabınızın restoran bağlantısı kapalı. Lütfen restoran yöneticinizle iletişime geçin.",
};

function WaiterLogin() {
  const navigate = useNavigate();
  const context = useContext(Context);

  const hasSaved = !!(localStorage.wtrUsername && localStorage.wtrPassword);
  const [remember, setRemember] = useState(hasSaved);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const [userdata, setUserdata] = useState<IUserLogin>({
    email: localStorage.wtrUsername ? decrypt(localStorage.wtrUsername) : "",
    password: localStorage.wtrPassword ? decrypt(localStorage.wtrPassword) : "",
    deviceId: getOrCreateDeviceId(),
  });

  useEffect(() => {
    // Web token al (anonim giriş uçları için)
    getTokenForWeb()
      .then((res) => {
        if (res?.data) localStorage.setItem("MA", encrypt(JSON.stringify(res.data)));
      })
      .catch((err) => console.log("error", err.message));
  }, []);

  const handleLogin = (evt: FormEvent) => {
    evt.preventDefault();
    if (!validUserLogin(userdata.email, userdata.password)) return;

    if (remember) {
      localStorage.setItem("wtrUsername", encrypt(userdata.email));
      localStorage.setItem("wtrPassword", encrypt(userdata.password));
    } else {
      localStorage.removeItem("wtrUsername");
      localStorage.removeItem("wtrPassword");
    }

    setErrorMessage("");
    setLoading(true);
    waiterLoginOps(userdata)
      .then(async (res) => {
        const tokenData = res.data;
        // Garson girişinde restoran yanıtta gelir; token yenilemede gelmediği için burada saklanır.
        const restourantId = tokenData.restourantId ?? "";
        const provisional: ILoginInfo = { ...tokenData, restourantId };
        localStorage.setItem("user", encrypt(JSON.stringify(provisional)));

        let access: IWaiterAccess;
        try {
          access = await checkWaiterAccess();
        } catch {
          localStorage.removeItem("user");
          setErrorMessage("Yetki bilgileri alınamadı. Lütfen tekrar deneyin.");
          setLoading(false);
          return;
        }

        if (!access.isWaiter && !access.isSuperAdmin && !access.isRestaurantAdmin) {
          localStorage.removeItem("user");
          setErrorMessage("Bu hesabın salon ekranına erişim yetkisi yok.");
          setLoading(false);
          return;
        }

        // Yalnızca saf garson hesapları salon ekranına kilitlenir; yönetici bu ekrandan girse
        // bile diğer sayfalara erişimini korur.
        const isWaiterOnly = access.isWaiter && !access.isSuperAdmin && !access.isRestaurantAdmin;

        // Garson rolü tek başına salonu açmıyor (backend 18 Eki): Salon Siparişi rolü de gerekli.
        // Olmadan girerse ekran açılır ama her işlem 403 alır — girişte açıkça söylemek daha iyi.
        if (isWaiterOnly && !access.hasSalonRole) {
          localStorage.removeItem("user");
          setErrorMessage("Hesabınıza Salon Siparişi yetkisi verilmemiş. Restoran yöneticiniz bu yetkiyi verdikten sonra tekrar giriş yapın.");
          setLoading(false);
          return;
        }
        const finalInfo: ILoginInfo = { ...tokenData, restourantId, isWaiterOnly };
        localStorage.setItem("user", encrypt(JSON.stringify(finalInfo)));

        try {
          const detailsRes = await getCustomerDetailsByToken();
          const merged = { ...finalInfo, ...detailsRes.data, restourantId, isWaiterOnly };
          localStorage.setItem("user", encrypt(JSON.stringify(merged)));
          context.setName(merged as any);
        } catch {
          context.setName(finalInfo as any);
        }

        navigate(isWaiterOnly ? "/garson" : "/salon", { replace: true });
      })
      .catch((err) => {
        localStorage.removeItem("user");
        const data = err?.response?.data;
        const first = Array.isArray(data) ? data[0] : null;
        setErrorMessage(
          (first?.code && ERROR_TEXT[first.code]) || first?.description || err.message
        );
      })
      .finally(() => setLoading(false));
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #f0fdfa 0%, #fafafa 60%, #ccfbf1 100%)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: "16px",
    }}>
      <div style={{
        width: "100%", maxWidth: "420px", background: "#fff", borderRadius: "20px",
        boxShadow: "0 8px 40px rgba(0,0,0,0.10)", padding: "44px 40px 36px", boxSizing: "border-box",
      }}>
        {/* Başlık */}
        <div className="text-center mb-4">
          <div style={{
            width: "56px", height: "56px", background: ACCENT, borderRadius: "16px",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            marginBottom: "16px", boxShadow: ACCENT_SHADOW,
          }}>
            <i className="bi bi-person-badge text-white" style={{ fontSize: "1.6rem" }}></i>
          </div>
          <h4 className="fw-bold mb-1" style={{ color: "#111827", letterSpacing: "-0.3px" }}>Garson Girişi</h4>
          <p className="text-muted small mb-0">Salon ekranına giriş yapın</p>
        </div>

        <LoginRoleSwitch active="garson" />

        {errorMessage && (
          <div className="d-flex align-items-center gap-2 rounded-3 px-3 py-2 mb-3"
            style={{ background: "#fef2f2", border: "1.5px solid #fecaca", color: "#b91c1c", fontSize: "0.85rem" }}>
            <i className="bi bi-exclamation-circle-fill flex-shrink-0"></i>
            <span>{errorMessage}</span>
            <button onClick={() => setErrorMessage("")}
              style={{ marginLeft: "auto", background: "none", border: "none", color: "#b91c1c", cursor: "pointer", padding: 0 }}>
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
        )}

        <form onSubmit={handleLogin}>
          <div className="mb-3">
            <label className="form-label small fw-semibold mb-1" style={{ color: "#374151" }}>E-posta</label>
            <div className="input-group">
              <span className="input-group-text"
                style={{ background: "#f9fafb", border: "1.5px solid #e5e7eb", borderRight: "none", borderRadius: "10px 0 0 10px", color: "#9ca3af" }}>
                <i className="bi bi-envelope-fill"></i>
              </span>
              <input type="email" value={userdata.email} autoComplete="username"
                onChange={(e) => setUserdata({ ...userdata, email: e.target.value })}
                className="form-control" placeholder="ornek@email.com" required
                style={{ border: "1.5px solid #e5e7eb", borderLeft: "none", borderRadius: "0 10px 10px 0", fontSize: "0.93rem", padding: "10px 14px", boxShadow: "none" }} />
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label small fw-semibold mb-1" style={{ color: "#374151" }}>Şifre</label>
            <div className="input-group">
              <span className="input-group-text"
                style={{ background: "#f9fafb", border: "1.5px solid #e5e7eb", borderRight: "none", borderRadius: "10px 0 0 10px", color: "#9ca3af" }}>
                <i className="bi bi-lock-fill"></i>
              </span>
              <input type={showPass ? "text" : "password"} value={userdata.password} autoComplete="current-password"
                onChange={(e) => setUserdata({ ...userdata, password: e.target.value })}
                className="form-control" placeholder="••••••••" required
                style={{ border: "1.5px solid #e5e7eb", borderLeft: "none", borderRight: "none", fontSize: "0.93rem", padding: "10px 14px", boxShadow: "none" }} />
              <button type="button" className="input-group-text" onClick={() => setShowPass(!showPass)}
                aria-label={showPass ? "Şifreyi gizle" : "Şifreyi göster"}
                style={{ background: "#f9fafb", border: "1.5px solid #e5e7eb", borderLeft: "none", borderRadius: "0 10px 10px 0", color: "#9ca3af", cursor: "pointer" }}>
                <i className={`bi ${showPass ? "bi-eye-slash-fill" : "bi-eye-fill"}`}></i>
              </button>
            </div>
          </div>

          <div className="d-flex align-items-center mb-4">
            <div onClick={() => setRemember(!remember)}
              style={{
                width: "18px", height: "18px", borderRadius: "5px",
                border: `2px solid ${remember ? "#0f766e" : "#d1d5db"}`, background: remember ? "#0f766e" : "#fff",
                display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, transition: "all 0.15s ease",
              }}>
              {remember && <i className="bi bi-check text-white" style={{ fontSize: "0.7rem", lineHeight: 1 }}></i>}
            </div>
            <label onClick={() => setRemember(!remember)} className="ms-2 small" style={{ color: "#6b7280", cursor: "pointer", userSelect: "none" }}>
              Beni hatırla
            </label>
          </div>

          <button type="submit" className="btn w-100" disabled={loading}
            style={{
              background: loading ? "#5eead4" : ACCENT, color: "#fff", fontWeight: 700, fontSize: "0.97rem",
              borderRadius: "10px", padding: "11px", border: "none", boxShadow: ACCENT_SHADOW, transition: "all 0.2s ease",
            }}>
            {loading
              ? <><span className="spinner-border spinner-border-sm me-2" role="status"></span>Giriş yapılıyor...</>
              : <><i className="bi bi-box-arrow-in-right me-2"></i>Giriş Yap</>}
          </button>
        </form>
      </div>
    </div>
  );
}

export default WaiterLogin;
